import { useEffect, useMemo, useRef, useState } from 'react';
import Toast, { useToast } from '../../components/Toast';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { getStaffTrainingRecords, getTrainingCourses, getStaffCourseRecords, certificationStatus, setOverride } from '../../lib/training';
import { notifyTrainingReminder } from '../../lib/notifications';
import { renderCourseCertificateBlob, blobToBase64 } from '../../lib/documents/trainingCertificatePdf';
import { renderContractingAgreementBlob } from '../../lib/documents/contractingAgreementPdf';
import { StatTile } from '../../components/StatTile';
import ProfileTabBar from '../../components/ProfileTabBar';
import DataTable from '../../components/DataTable';
import AssignTrainingModal from '../../components/training/AssignTrainingModal';

const CERT_TYPES = [
  { key: 'rn_lpn_licence',      label: 'RN/LPN Licence',              months: 24 },
  { key: 'cna_certification',   label: 'CNA Certification',           months: 24 },
  { key: 'cpr_first_aid',       label: 'CPR / First Aid',             months: 24 },
  { key: 'tb_screening',        label: 'TB Screening',                months: 12 },
  { key: 'gcic_background',     label: 'GCIC/FBI Background Check',   months: 60 },
  { key: 'hipaa_training',      label: 'HIPAA Training',              months: 12 },
  { key: 'inservice_training',  label: 'In-Service Training Hours',   months: 12 },
  { key: 'health_screening',    label: 'Annual Health Screening',     months: 12 },
  { key: 'hha_competency',      label: 'HHA Competency Evaluation',   months: null },
  { key: 'other',               label: 'Other',                       months: null },
];
const CERT_LABEL = Object.fromEntries(CERT_TYPES.map(c => [c.key, c.label]));
const EMPTY_FORM = { staff_id: '', cert_type: 'hipaa_training', cert_number: '', issued_date: '', expiry_date: '', notes: '' };

const StatusPill = ({ status }) => (
  <span style={{ fontSize: 11, fontWeight: 500, padding: '2px 8px', borderRadius: 20, background: status.bg, color: status.color }}>{status.label}</span>
);

export default function TrainingCompliance() {
  const { profile } = useAuth();
  const [tab, setTab] = useState('certifications');
  const [certs, setCerts] = useState([]);
  const [staffOptions, setStaffOptions] = useState([]);
  const [records, setRecords] = useState([]);
  const [courses, setCourses] = useState([]);
  const [courseRecords, setCourseRecords] = useState([]);
  const [annualHours, setAnnualHours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('All');
  const [staffFilter, setStaffFilter] = useState('');
  const [sortKey, setSortKey] = useState('expiry_date');
  const [sortAsc, setSortAsc] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const { toast, showToast, hideToast } = useToast();
  const [overrideTarget, setOverrideTarget] = useState(null);
  const [overrideForm, setOverrideForm] = useState({ reason: '', expiresAt: '' });
  const [assignTarget, setAssignTarget] = useState(null);
  const [selectedStaffId, setSelectedStaffId] = useState('');
  const [staffDropdownOpen, setStaffDropdownOpen] = useState(false);
  const [staffDropdownPos, setStaffDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const staffDropdownRef = useRef(null);
  const [certifying, setCertifying] = useState(null);
  const [reminding, setReminding] = useState(null);
  // Onboarding follow-ups (2026-09-07): everything that used to be a lane
  // after the signed bundle, now one row per person until it is all done.
  const [onboardingStaff, setOnboardingStaff] = useState([]);
  const [bundleDocs, setBundleDocs] = useState([]);
  const [axisFailures, setAxisFailures] = useState({});
  const [followupBusy, setFollowupBusy] = useState(null);
  const [expandedCourses, setExpandedCourses] = useState(new Set());

  // ── Contracting Agencies (Policy A-2.4) ─────────────────────────────────────
  const [agencies, setAgencies] = useState([]);
  const [agencyStaffCounts, setAgencyStaffCounts] = useState({});
  const [showAgencyModal, setShowAgencyModal] = useState(false);
  const [editingAgency, setEditingAgency] = useState(null);
  const [agencyForm, setAgencyForm] = useState({ name: '', status: 'pending', signed_date: '', annual_review_due_at: '', notes: '' });
  const [savingAgency, setSavingAgency] = useState(false);
  const [agencyPdfBusy, setAgencyPdfBusy] = useState(null);
  const [agencyUploadBusy, setAgencyUploadBusy] = useState(null);


  // One row per person with anything still open after (or before) signing.
  const followupRows = useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const now = Date.now();
    return onboardingStaff.map(s => {
      const isAgency = (s.hire_track || s.employment_type) === 'agency';
      const isNurse = ['RN', 'LPN'].includes(s.position_code);
      const clinical = ['RN', 'LPN', 'PCA', 'BHA'].includes(s.position_code);
      const docs = bundleDocs.filter(d => d.staff_id === s.id);
      const signed = docs.some(d => d.status === 'signed');
      const sentOut = docs.some(d => d.status === 'sent');
      const anchor = s.offer_signed_at || s.fast_tracked_at || s.created_at;
      const days = Math.floor((now - new Date(anchor).getTime()) / 86400000);
      const hasCert = t => certs.some(c => c.staff_id === s.id && c.cert_type === t && c.status !== 'requested' && (!c.expiry_date || c.expiry_date >= today));
      const myCourses = courseRecords.filter(r => r.staff_id === s.id);
      const openCourses = myCourses.filter(r => r.status !== 'completed').length;
      const items = [];
      if (!signed) items.push({ key: 'contract', label: sentOut ? 'Contract out for signature' : s.onboarding_status === 'candidate' ? 'Bundle not sent' : 'Contract unsigned', tone: 'danger' });
      if (!isAgency) items.push({ key: 'gusto', label: s.gusto_created_at ? 'Gusto ✓' : 'Gusto not confirmed', tone: s.gusto_created_at ? 'ok' : 'warn', action: !s.gusto_created_at });
      const axisFail = !s.axiscare_id && axisFailures[s.id];
      items.push({
        key: 'axiscare',
        label: s.axiscare_id ? `AxisCare ✓ (${s.axiscare_id})` : axisFail ? 'AxisCare auto-create failed' : signed ? 'No AxisCare account' : 'AxisCare: created at signing',
        tone: s.axiscare_id ? 'ok' : axisFail ? 'danger' : signed ? 'warn' : 'ok',
        title: axisFail ? `${axisFail} — fix the record, then use "Push to AxisCare" in Personnel.` : undefined,
      });
      if (s.bg_check_status === 'flagged') items.push({ key: 'bg', label: 'Background check FLAGGED', tone: 'danger' });
      else if (s.bg_check_status === 'cleared') items.push({ key: 'bg', label: isNurse ? 'License verified ✓' : 'GCHEXS cleared ✓', tone: 'ok' });
      else items.push({ key: 'bg', label: isNurse ? 'License not verified' : s.bg_check_status === 'pending' ? 'GCHEXS pending' : 'GCHEXS not started', tone: days > 30 ? 'danger' : 'warn' });
      if (clinical) {
        items.push({ key: 'cpr', label: hasCert('cpr_first_aid') ? 'CPR ✓' : 'CPR not on file', tone: hasCert('cpr_first_aid') ? 'ok' : 'warn' });
        items.push({ key: 'tb', label: hasCert('tb_screening') ? 'TB ✓' : 'TB not on file', tone: hasCert('tb_screening') ? 'ok' : 'warn' });
      }
      if (myCourses.length) items.push({ key: 'courses', label: openCourses === 0 ? 'Courses ✓' : `${openCourses} course${openCourses === 1 ? '' : 's'} open${days > 30 ? ' (overdue)' : ''}`, tone: openCourses === 0 ? 'ok' : days > 30 ? 'danger' : 'warn' });
      const open = items.filter(i => i.tone !== 'ok').length;
      return { staff: s, items, open, days, signed };
    }).filter(r => r.open > 0).sort((a, b) => b.days - a.days);
  }, [onboardingStaff, bundleDocs, certs, courseRecords, axisFailures]);

  const markGustoDone = async (s) => {
    setFollowupBusy(s.id);
    try {
      const { error } = await supabase.from('staff').update({ gusto_created_at: new Date().toISOString() }).eq('id', s.id);
      if (error) throw error;
      await supabase.from('onboarding_events').insert({ staff_id: s.id, event_type: 'gusto_created', event_detail: 'Marked created in Gusto', previous_status: null, new_status: null, actor: profile?.full_name || 'office' });
      showToast(`${s.full_name} marked as set up in Gusto.`);
      load();
    } catch (err) { showToast('Failed: ' + err.message, 'error'); }
    finally { setFollowupBusy(null); }
  };

  const load = async () => {
    setLoading(true);
    const since90 = new Date(); since90.setDate(since90.getDate() - 90);
    const [{ data: certRows }, { data: staffRows }, { data: recordRows }, { data: courseRows }, { data: courseRecordRows }, { data: hoursRows }, { data: agencyRows }, { data: agencyLinkRows }, { data: onboardingRows }, { data: bundleRows }] = await Promise.all([
      supabase.from('staff_certifications').select('*, staff(full_name, first_name, last_name)').order('expiry_date'),
      supabase.from('staff').select('id, full_name, first_name, last_name, position_code, email').eq('status', 'active').order('last_name'),
      getStaffTrainingRecords(),
      getTrainingCourses(),
      getStaffCourseRecords(),
      supabase.from('staff_annual_training_hours').select('*').eq('year', new Date().getFullYear()),
      supabase.from('contracting_agencies').select('*').order('name'),
      // Not filtered to status='active' — a candidate still onboarding through
      // an agency should still count against that agency's linked-staff total.
      supabase.from('staff').select('contracting_agency_id').not('contracting_agency_id', 'is', null),
      // People who signed (or were fast-tracked) in the last 90 days, plus
      // anyone still waiting to sign — the follow-up population.
      supabase.from('staff').select('id, full_name, first_name, last_name, position_code, email, status, onboarding_status, hire_track, employment_type, offer_sent_at, offer_signed_at, offer_expires_at, gusto_created_at, axiscare_id, bg_check_status, bg_check_type, bg_check_initiated_at, fast_tracked_at, created_at')
        .eq('hidden', false).neq('status', 'terminated')
        .or(`offer_signed_at.gte.${since90.toISOString()},onboarding_status.eq.offer_sent,onboarding_status.eq.candidate,and(status.eq.active,gusto_created_at.is.null,hire_track.neq.agency)`),
      supabase.from('staff_documents').select('staff_id, doc_type, status, signed_at').in('doc_type', ['offer_letter', 'job_description', 'background_check_consent', 'agency_worker_acknowledgment', 'policy_acknowledgment', 'confidentiality_agreement', 'orientation_attestation']),
    ]);
    setOnboardingStaff(onboardingRows || []);
    setBundleDocs(bundleRows || []);
    // AxisCare accounts are created automatically at signature; when that
    // failed the signing function logged why. Surface the latest reason so
    // the office knows what to fix before using "Push to AxisCare".
    const idsNoAxis = (onboardingRows || []).filter(r => !r.axiscare_id).map(r => r.id);
    if (idsNoAxis.length) {
      const { data: failRows } = await supabase.from('onboarding_events').select('staff_id, event_detail, created_at')
        .eq('event_type', 'axiscare_failed').in('staff_id', idsNoAxis).order('created_at', { ascending: false });
      const latest = {};
      (failRows || []).forEach(e => { if (!latest[e.staff_id]) latest[e.staff_id] = e.event_detail; });
      setAxisFailures(latest);
    } else {
      setAxisFailures({});
    }
    setCerts(certRows || []);
    setStaffOptions(staffRows || []);
    setRecords(recordRows || []);
    setCourses(courseRows || []);
    setAgencies(agencyRows || []);
    setAgencyStaffCounts((agencyLinkRows || []).reduce((acc, r) => { acc[r.contracting_agency_id] = (acc[r.contracting_agency_id] || 0) + 1; return acc; }, {}));
    setCourseRecords(courseRecordRows || []);
    setAnnualHours(hoursRows || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!staffDropdownOpen) return;
    const onClick = (e) => { if (staffDropdownRef.current && !staffDropdownRef.current.contains(e.target)) setStaffDropdownOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [staffDropdownOpen]);

  const openAdd = () => { setEditing(null); setForm(EMPTY_FORM); setShowModal(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ staff_id: c.staff_id, cert_type: c.cert_type, cert_number: c.cert_number || '', issued_date: c.issued_date || '', expiry_date: c.expiry_date || '', notes: c.notes || '' });
    setShowModal(true);
  };

  const handleSave = async () => {
    // hha_competency/other are deliberately no-expiry cert types —
    // certificationStatus() treats a missing expiry_date as permanently
    // Compliant for them, but this form required one for every type
    // regardless, making that code path unreachable from the UI.
    const expiryRequired = CERT_TYPES.find(c => c.key === form.cert_type)?.months != null;
    if (!form.staff_id || (expiryRequired && !form.expiry_date)) { showToast('Staff member and expiry date are required.', 'error'); return; }
    setSaving(true);
    const payload = {
      staff_id: form.staff_id, cert_type: form.cert_type, cert_label: CERT_LABEL[form.cert_type],
      cert_number: form.cert_number || null, issued_date: form.issued_date || null,
      expiry_date: form.expiry_date || null, notes: form.notes || null, updated_at: new Date().toISOString(),
    };
    const { error } = editing
      ? await supabase.from('staff_certifications').update(payload).eq('id', editing.id)
      : await supabase.from('staff_certifications').insert([payload]);
    setSaving(false);
    if (error) { showToast('Save failed: ' + error.message, 'error'); return; }
    setShowModal(false);
    showToast('Certification saved.');
    load();
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Remove this ${CERT_LABEL[c.cert_type]} record for ${c.staff?.full_name}?`)) return;
    const { error } = await supabase.from('staff_certifications').delete().eq('id', c.id);
    if (error) { showToast('Delete failed: ' + error.message, 'error'); return; }
    showToast('Removed.');
    load();
  };

  // ── Contracting Agencies (Policy A-2.4) ─────────────────────────────────────
  const openAgencyAdd = () => {
    setEditingAgency(null);
    setAgencyForm({ name: '', status: 'pending', signed_date: '', annual_review_due_at: '', notes: '' });
    setShowAgencyModal(true);
  };
  const openAgencyEdit = (a) => {
    setEditingAgency(a);
    setAgencyForm({ name: a.name, status: a.status, signed_date: a.signed_date || '', annual_review_due_at: a.annual_review_due_at || '', notes: a.notes || '' });
    setShowAgencyModal(true);
  };
  const saveAgency = async () => {
    if (!agencyForm.name.trim()) { showToast('Agency name is required.', 'error'); return; }
    setSavingAgency(true);
    const payload = { ...agencyForm, name: agencyForm.name.trim(), signed_date: agencyForm.signed_date || null, annual_review_due_at: agencyForm.annual_review_due_at || null, updated_at: new Date().toISOString() };
    const { error } = editingAgency
      ? await supabase.from('contracting_agencies').update(payload).eq('id', editingAgency.id)
      : await supabase.from('contracting_agencies').insert([payload]);
    setSavingAgency(false);
    if (error) { showToast('Save failed: ' + error.message, 'error'); return; }
    setShowAgencyModal(false);
    showToast('Contracting agency saved.');
    load();
  };

  // Downloads the unsigned agreement template for the office to execute
  // externally (SignWell/DocuSign) — see contractingAgreementPdf.js for why
  // this isn't an in-app e-signature flow.
  const downloadAgreementPdf = async (agencyName) => {
    setAgencyPdfBusy(agencyName);
    try {
      const blob = await renderContractingAgreementBlob(agencyName);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Subcontractor_Agreement_${agencyName.replace(/[^a-z0-9]+/gi, '_')}.pdf`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      showToast('PDF generation failed: ' + e.message, 'error');
    } finally {
      setAgencyPdfBusy(null);
    }
  };

  // Files the externally-executed agreement PDF to Drive and records
  // signed_pdf_url — the office uploads whatever came back from SignWell/
  // DocuSign here once it's fully signed.
  const uploadSignedAgreement = async (agency, file) => {
    if (!file) return;
    setAgencyUploadBusy(agency.id);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const fileBase64 = btoa(new Uint8Array(arrayBuffer).reduce((s, b) => s + String.fromCharCode(b), ''));
      const { data, error } = await supabase.functions.invoke('upload-agency-agreement', {
        body: { fileBase64, filename: file.name, agencyId: agency.id },
      });
      if (error || !data?.success) throw new Error(data?.error || error?.message || 'Upload failed');
      showToast('Signed agreement filed.');
      load();
    } catch (e) {
      showToast('Upload failed: ' + e.message, 'error');
    } finally {
      setAgencyUploadBusy(null);
    }
  };

  const openOverride = (c) => { setOverrideTarget(c); setOverrideForm({ reason: c.override_reason || '', expiresAt: c.override_expires_at || '' }); };
  const saveOverride = async () => {
    if (!overrideForm.reason || !overrideForm.expiresAt) { showToast('Reason and a grace-period end date are required.', 'error'); return; }
    await setOverride({ certId: overrideTarget.id, active: true, reason: overrideForm.reason, expiresAt: overrideForm.expiresAt, overrideBy: profile.id });
    setOverrideTarget(null);
    showToast('Override applied — this is a bounded grace period, not a permanent bypass.');
    load();
  };
  const clearOverride = async (c) => {
    await setOverride({ certId: c.id, active: false, overrideBy: profile.id });
    showToast('Override cleared.');
    load();
  };

  // Backfills the ONE consolidated auditor-presentable certificate for a course
  // that was completed before this Drive-filing feature existed — real LMS tools
  // certify at the course level, not per section, so this lists every section's
  // own completion date/signature inside one PDF (see renderCourseCertificateBlob).
  const generateCourseCertificate = async (courseRecord, course, sections, staffName) => {
    setCertifying(courseRecord.id);
    try {
      const blob = await renderCourseCertificateBlob({
        staffName,
        courseTitle: course.title,
        courseDescription: course.description,
        completedAt: courseRecord.completed_at ? new Date(courseRecord.completed_at).toLocaleString() : new Date().toLocaleString(),
        signatureRef: sections[sections.length - 1]?.signature_ref || staffName,
        courseRecordId: courseRecord.id,
        sections: sections.map(s => ({
          moduleNumber: s.training_module?.module_number,
          title: s.training_module?.title,
          completedAt: s.completed_at ? new Date(s.completed_at).toLocaleDateString() : '—',
        })),
      });
      const fileBase64 = await blobToBase64(blob);
      // Uses supabase.functions.invoke (not raw fetch) so the coordinator's own
      // session JWT is attached — the edge function authorizes this backfill path
      // by verifying the caller is coordinator+, since there's no training-link
      // token in this authenticated-page flow.
      const { data: j, error: fnErr } = await supabase.functions.invoke('upload-training-certificate', {
        body: {
          fileBase64,
          filename: `${course.title.replace(/[^a-z0-9]+/gi, '_')}_${staffName.replace(/[^a-z0-9]+/gi, '_')}.pdf`,
          staffName, courseRecordId: courseRecord.id,
          contentType: 'application/pdf',
        },
      });
      if (fnErr || !j?.success) throw new Error(j?.error || fnErr?.message || 'Certificate filing failed');
      showToast('Certificate filed to Drive.');
      load();
    } catch (e) {
      showToast('Certificate generation failed: ' + e.message, 'error');
    } finally {
      setCertifying(null);
    }
  };

  // Manual reminder — reuses the same no-login token the original assignment
  // email used (one link per person covers everything currently pending),
  // so this doesn't create a second link or re-enroll anything.
  const sendCourseReminder = async (courseRecord, course, staffMember) => {
    setReminding(courseRecord.id);
    try {
      // .limit(1) instead of .maybeSingle() — defensive against any pre-existing
      // duplicate 'sent' rows (a DB unique index now prevents new ones, but this
      // stays resilient rather than throwing on "multiple rows returned").
      const { data: docs } = await supabase.from('staff_documents')
        .select('sign_token').eq('staff_id', staffMember.id).eq('doc_type', 'training').eq('status', 'sent')
        .order('sent_at', { ascending: false }).limit(1);
      const doc = docs?.[0];
      if (!doc?.sign_token) throw new Error('No active training link found for this staff member — use Assign to create one.');
      if (!staffMember.email) throw new Error('This staff member has no email on file.');
      await notifyTrainingReminder(staffMember.email, staffMember.full_name, course.title, `${window.location.origin}/training-link/${doc.sign_token}`);
      showToast(`Reminder sent to ${staffMember.full_name}.`);
    } catch (e) {
      showToast('Reminder failed: ' + e.message, 'error');
    } finally {
      setReminding(null);
    }
  };

  const sorting = useMemo(() => sortKey ? [{ id: sortKey, desc: !sortAsc }] : [], [sortKey, sortAsc]);
  const handleSortingChange = (updaterOrValue) => {
    const next = typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
    if (!next.length) return;
    setSortKey(next[0].id);
    setSortAsc(!next[0].desc);
  };

  // "Compliant" is a grouped bucket (compliant + valid + override), not a
  // single certificationStatus() key — the "Fully Compliant" KPI tile below
  // and the "Compliant" filter chip must agree on that grouping, or the tile
  // count and the filtered list directly contradict each other.
  const COMPLIANT_STATUS_KEYS = new Set(['compliant', 'valid', 'override']);
  const matchesFilter = (statusKey) => filter === 'All' || (filter === 'compliant' ? COMPLIANT_STATUS_KEYS.has(statusKey) : statusKey === filter);

  const withStatus = certs.map(c => ({ ...c, _status: certificationStatus(c) }));
  const filtered = withStatus
    .filter(c => matchesFilter(c._status.key))
    .filter(c => !staffFilter || c.staff_id === staffFilter)
    .sort((a, b) => {
      let av = sortKey === 'staff' ? (a.staff?.last_name || a.staff?.full_name) : a[sortKey];
      let bv = sortKey === 'staff' ? (b.staff?.last_name || b.staff?.full_name) : b[sortKey];
      if (av == null) return 1; if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv));
      return sortAsc ? cmp : -cmp;
    });

  const stats = {
    expired: withStatus.filter(c => c._status.key === 'expired').length,
    expiring: withStatus.filter(c => c._status.key === 'expiring').length,
    compliant: withStatus.filter(c => COMPLIANT_STATUS_KEYS.has(c._status.key)).length,
    // Matches the Modules tab's own overdue definition (status !== 'completed',
    // not just 'assigned') — a started-but-not-finished record past its due
    // date is still overdue, and the Modules tab already flags it as such.
    overdueTraining: records.filter(r => r.status !== 'completed' && r.due_date && r.due_date < new Date().toISOString().split('T')[0]).length,
  };

  const certColumns = [
    { id: 'staff', header: 'Staff', size: 160, meta: { mobile: 'primary' }, cell: info => { const s = info.row.original.staff; return s ? (s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name) : '—'; } },
    { id: 'cert_label', header: 'Certification', size: 180, enableSorting: false, meta: { mobile: 'primary' }, cell: info => info.row.original.cert_label || CERT_LABEL[info.row.original.cert_type] },
    { accessorKey: 'expiry_date', header: 'Expires', size: 100, meta: { mobile: 'secondary' } },
    { id: 'status', header: 'Status', size: 150, enableSorting: false, meta: { mobile: 'primary' }, cell: info => <StatusPill status={info.row.original._status} /> },
    {
      id: 'actions', header: '', size: 170, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => {
        const c = info.row.original;
        return (
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent2)', fontSize: 12, padding: 0 }}>Edit</button>
            {c.override_active
              ? <button onClick={() => clearOverride(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--copper)', fontSize: 12, padding: 0 }}>Clear override</button>
              : <button onClick={() => openOverride(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--copper)', fontSize: 12, padding: 0 }}>Override</button>}
            <button onClick={() => handleDelete(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--danger)', fontSize: 12, padding: 0 }}>Remove</button>
          </div>
        );
      },
    },
  ];

  // Full active roster, not just staff who already have a training record — otherwise
  // there's no way to pick someone to assign their *first* module to.
  const staffList = staffOptions;
  const selectedStaff = staffList.find(s => s.id === selectedStaffId) || null;
  const staffRecords = records.filter(r => r.staff_id === selectedStaff?.id);
  const staffCourseRecords = courseRecords.filter(r => r.staff_id === selectedStaff?.id);
  const staffCerts = certs.filter(c => c.staff_id === selectedStaff?.id);
  const staffCompliancePct = staffRecords.length ? Math.round((staffRecords.filter(r => r.status === 'completed').length / staffRecords.length) * 100) : 0;

  return (
    <div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 8, marginBottom: 14 }}>
        <StatTile label="Certs Expired" value={stats.expired} tone={stats.expired > 0 ? 'alert' : 'neutral'} />
        <StatTile label="Certs Expiring ≤30d" value={stats.expiring} tone={stats.expiring > 0 ? 'warn' : 'neutral'} />
        <StatTile label="Fully Compliant" value={stats.compliant} />
        <StatTile label="Trainings Overdue" value={stats.overdueTraining} tone={stats.overdueTraining > 0 ? 'warn' : 'neutral'} />
      </div>

      <div className="card">
        <ProfileTabBar
          tabs={[
            { key: 'certifications', label: 'Certifications', badge: certs.length },
            { key: 'modules', label: 'Training Courses', badge: courses.length },
            { key: 'staff', label: 'By Staff Member' },
            { key: 'agencies', label: 'Contracting Agencies', badge: agencies.length },
            { key: 'followups', label: 'Onboarding follow-ups', badge: followupRows.length },
          ]}
          activeKey={tab}
          onSelect={setTab}
        />

        {tab === 'certifications' && (
          <>
            <div style={{ padding: '10px 16px', display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', borderBottom: '0.5px solid var(--border)' }}>
              <div style={{ display: 'flex', gap: 6 }}>
                {['All', 'expired', 'expiring', 'compliant'].map(f => (
                  <button key={f} onClick={() => setFilter(f)} style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: filter === f ? 'var(--accent)' : 'transparent', color: filter === f ? '#fff' : 'var(--text3)', borderColor: filter === f ? 'var(--accent)' : 'var(--border)' }}>
                    {{ All: 'All', expired: 'Expired', expiring: 'Expiring', compliant: 'Compliant' }[f]}
                  </button>
                ))}
              </div>
              <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} style={{ padding: '5px 8px', borderRadius: 8, fontSize: 12, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)' }}>
                <option value="">All staff</option>
                {staffOptions.map(s => <option key={s.id} value={s.id}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</option>)}
              </select>
              <button onClick={openAdd} style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}>+ Add Certification</button>
            </div>
            <DataTable tableId="training-certs" columns={certColumns} data={filtered} getRowId={c => c.id} sorting={sorting} onSortingChange={handleSortingChange} manualSorting loading={loading} emptyMessage="No certification records match this filter." maxHeight="calc(100vh - 380px)" />
          </>
        )}

        {tab === 'modules' && (
          <div style={{ overflowX: 'auto', padding: 12 }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
              <thead>
                <tr>
                  <th style={{ position: 'sticky', left: 0, background: 'var(--card-bg)', textAlign: 'left', padding: '8px 10px', fontSize: 11, color: 'var(--text3)' }}>Staff</th>
                  {courses.map(c => (
                    <th key={c.id} title={c.title} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 600, color: 'var(--text2)', textAlign: 'center', minWidth: 150, maxWidth: 180, whiteSpace: 'normal', lineHeight: 1.3 }}>{c.title}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {staffList.map(s => (
                  <tr key={s.id} style={{ borderTop: '0.5px solid var(--border)' }}>
                    <td style={{ position: 'sticky', left: 0, background: 'var(--card-bg)', padding: '8px 10px', fontSize: 12, whiteSpace: 'nowrap' }}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</td>
                    {courses.map(c => {
                      const courseRecord = courseRecords.find(r => r.staff_id === s.id && r.course_id === c.id);
                      const sections = records.filter(r => r.staff_id === s.id && r.training_module?.course_id === c.id);
                      const doneCount = sections.filter(r => r.status === 'completed').length;
                      const overdue = sections.some(r => r.status !== 'completed' && r.due_date && r.due_date < new Date().toISOString().split('T')[0]);
                      const dot = !courseRecord ? { bg: 'var(--bg2)', title: 'Not assigned' }
                        : courseRecord.status === 'completed' ? { bg: 'var(--success)', title: `Completed (${doneCount}/${sections.length})` }
                        : overdue ? { bg: 'var(--danger)', title: `Overdue (${doneCount}/${sections.length})` }
                        : { bg: 'var(--warn)', title: `In progress (${doneCount}/${sections.length})` };
                      return <td key={c.id} style={{ textAlign: 'center' }}><span title={dot.title} style={{ display: 'inline-block', width: 10, height: 10, borderRadius: '50%', background: dot.bg }} /></td>;
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === 'staff' && (
          <div style={{ padding: 16 }}>
            <div ref={staffDropdownRef} style={{ position: 'relative', display: 'inline-block', marginBottom: 16 }}>
              <button
                type="button"
                onClick={(e) => {
                  const r = e.currentTarget.getBoundingClientRect();
                  setStaffDropdownPos({ top: r.bottom + 4, left: r.left, width: r.width });
                  setStaffDropdownOpen(o => !o);
                }}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, minWidth: 220, padding: '6px 12px', borderRadius: 8, fontSize: 12, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: selectedStaff ? 'var(--text)' : 'var(--text3)', cursor: 'pointer', fontFamily: 'inherit' }}
              >
                <span>{selectedStaff ? (selectedStaff.last_name ? `${selectedStaff.last_name}, ${selectedStaff.first_name}` : selectedStaff.full_name) : 'Select a staff member…'}</span>
                <span style={{ fontSize: 10, color: 'var(--text3)' }}>▾</span>
              </button>
              {staffDropdownOpen && (
                // position: fixed (not absolute) — the surrounding .card has
                // overflow:hidden for its rounded corners, which would clip an
                // absolutely-positioned panel taller than the card's own
                // remaining space. Fixed positioning, coordinates taken from
                // the button's real screen position on open, escapes that.
                <div style={{ position: 'fixed', top: staffDropdownPos.top, left: staffDropdownPos.left, minWidth: Math.max(260, staffDropdownPos.width), maxHeight: 320, overflowY: 'auto', background: 'var(--topbar-bg)', border: '1px solid var(--border)', borderRadius: 8, boxShadow: '0 4px 16px rgba(13,43,78,0.12)', zIndex: 1000 }}>
                  {staffList.map(s => (
                    <div
                      key={s.id}
                      onClick={() => { setSelectedStaffId(s.id); setStaffDropdownOpen(false); }}
                      style={{ padding: '8px 12px', cursor: 'pointer', fontSize: 12.5, color: 'var(--text)', borderBottom: '0.5px solid var(--border)', background: selectedStaff?.id === s.id ? 'var(--accent-bg)' : 'transparent' }}
                      onMouseEnter={e => { if (selectedStaff?.id !== s.id) e.currentTarget.style.background = 'var(--bg)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = selectedStaff?.id === s.id ? 'var(--accent-bg)' : 'transparent'; }}
                    >
                      {s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {!selectedStaff ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '20px 0' }}>Select a staff member above to view their training and certification record.</div>
            ) : (
              <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
              <div>
                <div style={{ fontSize: 26, fontWeight: 500 }}>{staffCompliancePct}%</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>overall training compliance · {selectedStaff.position_code || 'position pending'}</div>
              </div>
              <div>
                <div style={{ fontSize: 26, fontWeight: 500 }}>{(annualHours.find(h => h.staff_id === selectedStaff.id)?.hours) || 0}h</div>
                <div style={{ fontSize: 12, color: 'var(--text3)' }}>documented in-service hours, {new Date().getFullYear()}</div>
              </div>
            </div>

            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', marginBottom: 6 }}>Certifications</div>
            <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 20, fontSize: 12 }}>
              <tbody>
                {staffCerts.map(c => (
                  <tr key={c.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <td style={{ padding: '6px 8px' }}>{c.cert_label}</td>
                    <td style={{ padding: '6px 8px' }}>{c.expiry_date}</td>
                    <td style={{ padding: '6px 8px' }}><StatusPill status={certificationStatus(c)} /></td>
                    <td style={{ padding: '6px 8px' }}><button onClick={() => openEdit(c)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent2)', fontSize: 12 }}>Renew →</button></td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase' }}>Training courses</div>
              <button onClick={() => setAssignTarget(selectedStaff)} style={{ marginLeft: 'auto', padding: '4px 10px', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}>+ Assign →</button>
            </div>
            {staffCourseRecords.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '8px 0 16px' }}>No training assigned yet.</div>
            ) : staffCourseRecords.map(cr => {
              const course = courses.find(c => c.id === cr.course_id);
              const sections = staffRecords.filter(r => r.training_module?.course_id === cr.course_id).sort((a, b) => (a.training_module?.sequence || 0) - (b.training_module?.sequence || 0));
              const doneCount = sections.filter(s => s.status === 'completed').length;
              const multiSection = sections.length > 1;
              const expanded = expandedCourses.has(cr.id);
              const toggleExpanded = () => setExpandedCourses(prev => {
                const next = new Set(prev);
                next.has(cr.id) ? next.delete(cr.id) : next.add(cr.id);
                return next;
              });
              return (
                <div key={cr.id} style={{ border: '0.5px solid var(--border)', borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: 'var(--bg2)', cursor: multiSection ? 'pointer' : 'default' }} onClick={multiSection ? toggleExpanded : undefined}>
                    {multiSection && (
                      <span style={{ color: 'var(--text3)', fontSize: 10, transition: 'transform 0.15s', transform: expanded ? 'rotate(90deg)' : 'none', display: 'inline-block' }}>▶</span>
                    )}
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{course?.title || cr.course?.title}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{doneCount}/{sections.length} section{sections.length === 1 ? '' : 's'} · {cr.status}{cr.completed_at ? ` · ${new Date(cr.completed_at).toLocaleDateString()}` : ''}</div>
                    </div>
                    {cr.pdf_url ? (
                      <a href={cr.pdf_url} target="_blank" rel="noreferrer" onClick={e => e.stopPropagation()} style={{ color: 'var(--accent2)', fontSize: 12 }}>Certificate →</a>
                    ) : cr.status === 'completed' ? (
                      <button onClick={e => { e.stopPropagation(); generateCourseCertificate(cr, course, sections, selectedStaff.full_name); }} disabled={certifying === cr.id}
                        style={{ background: 'none', border: 'none', cursor: certifying === cr.id ? 'default' : 'pointer', color: 'var(--accent2)', fontSize: 12, padding: 0 }}>
                        {certifying === cr.id ? 'Filing…' : 'Generate certificate'}
                      </button>
                    ) : (
                      <button onClick={e => { e.stopPropagation(); sendCourseReminder(cr, course, selectedStaff); }} disabled={reminding === cr.id}
                        style={{ background: 'none', border: 'none', cursor: reminding === cr.id ? 'default' : 'pointer', color: '#D97706', fontSize: 12, padding: 0 }}>
                        {reminding === cr.id ? 'Sending…' : 'Send Reminder'}
                      </button>
                    )}
                  </div>
                  {multiSection && expanded && (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <tbody>
                        {sections.map(s => (
                          <tr key={s.id} style={{ borderTop: '0.5px solid var(--border)' }}>
                            <td style={{ padding: '5px 12px', color: 'var(--text3)', width: 30 }}>{s.training_module?.module_number}</td>
                            <td style={{ padding: '5px 8px' }}>{s.training_module?.title}</td>
                            <td style={{ padding: '5px 8px', textAlign: 'right', color: 'var(--text3)' }}>{s.status}{s.completed_at ? ` · ${new Date(s.completed_at).toLocaleDateString()}` : ''}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
              </>
            )}
          </div>
        )}

        {tab === 'followups' && (
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, maxWidth: 680 }}>
              Signing the offer bundle is the only onboarding gate. Everything below is a follow-up after it — worked here, chased by the daily 8:30 AM email, never a reason to hold someone off the schedule. A flagged background check or a 30-day provisional window that has lapsed are the only two that should stop scheduling.
            </div>
            {followupRows.length === 0 ? (
              <div style={{ fontSize: 13, color: 'var(--success)', padding: '20px 0' }}>✓ Nothing open — everyone who signed in the last 90 days is fully set up.</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                  <thead>
                    <tr style={{ background: 'var(--bg2)' }}>
                      {['Person', 'Track', 'Days since signing', 'Open', 'Status of each item', ''].map(h => (
                        <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text2)', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {followupRows.map(({ staff: s, items, open, days, signed }) => (
                      <tr key={s.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          <a href={`/hr/personnel/${s.id}`} style={{ color: 'var(--text)', fontWeight: 600, textDecoration: 'none' }}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</a>
                          <div style={{ fontSize: 10, color: 'var(--text3)' }}>{s.position_code || '—'}{s.fast_tracked_at ? ' · ⚡ fast-tracked' : ''}</div>
                        </td>
                        <td style={{ padding: '8px 10px', textTransform: 'capitalize', whiteSpace: 'nowrap' }}>{s.hire_track || s.employment_type || '—'}</td>
                        <td style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums', color: !signed ? 'var(--text3)' : days > 30 ? 'var(--danger)' : 'var(--text)' }}>{signed ? days : '—'}</td>
                        <td style={{ padding: '8px 10px', fontVariantNumeric: 'tabular-nums' }}>{open}</td>
                        <td style={{ padding: '8px 10px' }}>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {items.map(i => (
                              <span key={i.key} title={i.title} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, whiteSpace: 'nowrap', cursor: i.title ? 'help' : 'default',
                                background: i.tone === 'ok' ? 'var(--success-bg)' : i.tone === 'danger' ? 'var(--danger-bg)' : 'var(--warn-bg)',
                                color: i.tone === 'ok' ? 'var(--success)' : i.tone === 'danger' ? 'var(--danger)' : 'var(--warn)' }}>{i.label}</span>
                            ))}
                          </div>
                        </td>
                        <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                          {items.some(i => i.key === 'gusto' && i.action) && (
                            <button onClick={() => markGustoDone(s)} disabled={followupBusy === s.id}
                              style={{ padding: '4px 10px', borderRadius: 8, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid var(--success-border)', background: 'transparent', color: 'var(--success)' }}>
                              {followupBusy === s.id ? 'Saving…' : 'Gusto done'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'agencies' && (
          <div style={{ padding: 16 }}>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 14, maxWidth: 640 }}>
              One signed agreement per staffing agency (Policy A-2.4) — not per individual. Each contracted worker they send still needs their own background-check documentation, TB/CPR, and full Orientation &amp; Training, tracked on their Personnel Profile as usual.
            </div>
            <button onClick={openAgencyAdd} style={{ marginBottom: 14, padding: '6px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', border: 'none', background: 'var(--accent)', color: '#fff' }}>+ Add Contracting Agency</button>
            {agencies.length === 0 ? (
              <div style={{ fontSize: 12, color: 'var(--text3)', padding: '20px 0' }}>No contracting agencies on file yet.</div>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <thead>
                  <tr style={{ borderBottom: '0.5px solid var(--border)' }}>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Agency</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Status</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Signed</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Annual review due</th>
                    <th style={{ textAlign: 'left', padding: '6px 8px', color: 'var(--text3)', fontWeight: 600 }}>Linked staff</th>
                    <th style={{ padding: '6px 8px' }}></th>
                  </tr>
                </thead>
                <tbody>
                  {agencies.map(a => {
                    const overdue = a.annual_review_due_at && a.annual_review_due_at < new Date().toISOString().split('T')[0];
                    return (
                      <tr key={a.id} style={{ borderBottom: '0.5px solid var(--border)' }}>
                        <td style={{ padding: '8px' }}>{a.name}</td>
                        <td style={{ padding: '8px', textTransform: 'capitalize' }}>{a.status}</td>
                        <td style={{ padding: '8px' }}>
                          {a.signed_pdf_url ? <a href={a.signed_pdf_url} target="_blank" rel="noreferrer" style={{ color: 'var(--accent2)' }}>{a.signed_date || 'View →'}</a> : <span style={{ color: 'var(--text3)' }}>Not on file</span>}
                        </td>
                        <td style={{ padding: '8px', color: overdue ? 'var(--danger)' : 'var(--text)' }}>{a.annual_review_due_at || '—'}{overdue ? ' ⚠' : ''}</td>
                        <td style={{ padding: '8px' }}>{agencyStaffCounts[a.id] || 0}</td>
                        <td style={{ padding: '8px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                          <button onClick={() => downloadAgreementPdf(a.name)} disabled={agencyPdfBusy === a.name} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent2)', fontSize: 12, padding: 0 }}>{agencyPdfBusy === a.name ? 'Generating…' : 'Download agreement'}</button>
                          <label style={{ color: 'var(--accent2)', fontSize: 12, cursor: 'pointer' }}>
                            {agencyUploadBusy === a.id ? 'Uploading…' : 'Upload signed'}
                            <input type="file" accept="application/pdf" style={{ display: 'none' }} disabled={agencyUploadBusy === a.id} onChange={e => uploadSignedAgreement(a, e.target.files?.[0])} />
                          </label>
                          <button onClick={() => openAgencyEdit(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent2)', fontSize: 12, padding: 0 }}>Edit</button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>

      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowModal(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>{editing ? 'Edit certification' : 'Add certification'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Staff member *</div>
                <select value={form.staff_id} onChange={e => setForm(p => ({ ...p, staff_id: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }}>
                  <option value="">Select staff…</option>
                  {staffOptions.map(s => <option key={s.id} value={s.id}>{s.last_name ? `${s.last_name}, ${s.first_name}` : s.full_name}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Certification type</div>
                <select value={form.cert_type} onChange={e => setForm(p => ({ ...p, cert_type: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }}>
                  {CERT_TYPES.map(c => <option key={c.key} value={c.key}>{c.label}{c.months ? ` (${c.months / 12}yr)` : ''}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Certification / licence number</div>
                <input value={form.cert_number} onChange={e => setForm(p => ({ ...p, cert_number: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Issued date</div>
                  <input type="date" value={form.issued_date} onChange={e => setForm(p => ({ ...p, issued_date: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>
                    Expiry date {CERT_TYPES.find(c => c.key === form.cert_type)?.months != null ? '*' : '(no expiry for this type)'}
                  </div>
                  <input type="date" value={form.expiry_date} onChange={e => setForm(p => ({ ...p, expiry_date: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Notes</div>
                <textarea rows={2} value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowModal(false)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ flex: 2, padding: '10px 0', background: saving ? '#aaa' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: saving ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {saving ? 'Saving…' : editing ? 'Save changes' : 'Add certification'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAgencyModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setShowAgencyModal(false)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 440, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 16 }}>{editingAgency ? 'Edit contracting agency' : 'Add contracting agency'}</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Agency name *</div>
                <input value={agencyForm.name} onChange={e => setAgencyForm(p => ({ ...p, name: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Status</div>
                <select value={agencyForm.status} onChange={e => setAgencyForm(p => ({ ...p, status: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)' }}>
                  <option value="pending">Pending</option>
                  <option value="active">Active</option>
                  <option value="expired">Expired</option>
                  <option value="terminated">Terminated</option>
                </select>
              </div>
              <div style={{ display: 'flex', gap: 10 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Signed date</div>
                  <input type="date" value={agencyForm.signed_date} onChange={e => setAgencyForm(p => ({ ...p, signed_date: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Annual review due</div>
                  <input type="date" value={agencyForm.annual_review_due_at} onChange={e => setAgencyForm(p => ({ ...p, annual_review_due_at: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
                </div>
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Notes</div>
                <textarea rows={2} value={agencyForm.notes} onChange={e => setAgencyForm(p => ({ ...p, notes: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setShowAgencyModal(false)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveAgency} disabled={savingAgency} style={{ flex: 2, padding: '10px 0', background: savingAgency ? '#aaa' : 'var(--accent)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: savingAgency ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                {savingAgency ? 'Saving…' : editingAgency ? 'Save changes' : 'Add agency'}
              </button>
            </div>
          </div>
        </div>
      )}

      {overrideTarget && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }} onClick={() => setOverrideTarget(null)}>
          <div style={{ background: 'var(--bg)', borderRadius: 12, width: '100%', maxWidth: 420, padding: 28, boxShadow: '0 8px 32px rgba(0,0,0,0.18)' }} onClick={e => e.stopPropagation()}>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text)', marginBottom: 4 }}>Manual compliance override</div>
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 16 }}>
              {overrideTarget.staff?.full_name} · {overrideTarget.cert_label} — this grants a bounded grace period, not a permanent bypass. It's logged with your name and a reason.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Reason *</div>
                <textarea rows={2} value={overrideForm.reason} onChange={e => setOverrideForm(p => ({ ...p, reason: e.target.value }))} placeholder="e.g. Renewal submitted 6/1, awaiting state processing" style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>Grace period ends *</div>
                <input type="date" value={overrideForm.expiresAt} onChange={e => setOverrideForm(p => ({ ...p, expiresAt: e.target.value }))} style={{ width: '100%', border: '0.5px solid var(--border)', borderRadius: 6, padding: '8px 10px', fontSize: 13, fontFamily: 'inherit', background: 'var(--bg)', color: 'var(--text)', boxSizing: 'border-box' }} />
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, marginTop: 20 }}>
              <button onClick={() => setOverrideTarget(null)} style={{ flex: 1, padding: '10px 0', background: 'transparent', color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 8, fontWeight: 500, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
              <button onClick={saveOverride} style={{ flex: 2, padding: '10px 0', background: 'var(--copper)', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Apply override</button>
            </div>
          </div>
        </div>
      )}

      {assignTarget && (
        <AssignTrainingModal
          staff={assignTarget}
          courses={courses}
          existingCourseRecords={courseRecords.filter(r => r.staff_id === assignTarget.id)}
          onClose={() => setAssignTarget(null)}
          onAssigned={() => { setAssignTarget(null); showToast('Training assigned.'); load(); }}
        />
      )}

      <Toast {...(toast || {})} onClose={hideToast} />
    </div>
  );
}
